import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useEffect, useRef } from "react";
import { ethers } from "ethers";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { MUCHFI_CHIKYU } from "../../config/muchfi";
import { fetchMuchfiFee } from "../../joinsplit-v2/muchfiFee";
import { privateSwapDeployment } from "../../joinsplit-v2/privateSwapConfig";
import { privateSpendSource } from "../../joinsplit-v2/privatePools";
import { requestPrivateFees } from "../../utils/relayer-client";
import type { PrivateSwapFeeQuote } from "../../joinsplit-v2/privateSwap";
import type { StoredJoinSplitNote } from "../../joinsplit/types";
import { privateFeePreviewRequest } from "../helpers/privateFeePreview";
import {
  quotePrivateSwap,
  type PrivateSwapQuote,
} from "../../joinsplit-v2/uniswap";

export interface OdosQuoteResponse {
  privateFeesSwap?: PrivateSwapFeeQuote;
  privateSwap?: PrivateSwapQuote;
  pathId: string;
  outAmounts: string[];
  outValues: number[];
  gasEstimate: number;
  priceImpact: number;
  percentDiff: number;
  netOutValue: number;
  blockNumber: number;
}

interface UseBaseSwapQuoteParams {
  notes?: StoredJoinSplitNote[];
  refreshKey?: number;
  enabled?: boolean;
  locked?: boolean;
  swapAmount: string;
  swapInputToken: TokenInfo | undefined;
  swapOutputToken: TokenInfo | undefined;
  swapSlippageBps: string;
  setSwapQuote: (value: OdosQuoteResponse | null) => void;
  setSwapQuoteLoading: (value: boolean) => void;
  setSwapQuoteError: (value: string | null) => void;
}

const ODOS_NATIVE_ADDRESS = "0x0000000000000000000000000000000000000000";
const BASE_WETH_ADDRESS = "0x4200000000000000000000000000000000000006";
const UNISWAP_V3_QUOTER_BASE_MAINNET =
  "0x3d4e44Eb1374240CE5F1B871ab261CD16335B76a";
const UNISWAP_V3_QUOTER_BASE_SEPOLIA =
  "0xC5290058841028F1614F3A6F0F5816cAd0df5E27";
const DEFAULT_UNISWAP_FEE_TIERS = [500, 3000, 10000];
const DEBOUNCE_MS = AMOUNT_QUOTE_DEBOUNCE_MS;
const NO_NOTES: StoredJoinSplitNote[] = [];
const SWAP_PROVIDER_OVERRIDE = (
  process.env.REACT_APP_BASE_SWAP_PROVIDER || ""
).toLowerCase();
const SWAP_DEBUG_PREFIX = "[BaseSwapQuote]";

function isUniswapDirectProvider(provider: string): boolean {
  return provider === "uniswap" || provider === "uniswap-direct";
}

function tokenToOdosAddress(token: TokenInfo): string {
  return token.isNative ? ODOS_NATIVE_ADDRESS : token.address;
}

function tokenToUniswapAddress(token: TokenInfo): string {
  return token.isNative ? BASE_WETH_ADDRESS : token.address;
}

function getUniswapQuoterAddress(): string {
  if (EVM_CONFIG.CHAIN_ID === 84532) {
    return (
      process.env.REACT_APP_BASE_UNISWAP_QUOTER ||
      UNISWAP_V3_QUOTER_BASE_SEPOLIA
    );
  }
  if (EVM_CONFIG.CHAIN_ID === 8453) {
    return (
      process.env.REACT_APP_BASE_UNISWAP_QUOTER ||
      UNISWAP_V3_QUOTER_BASE_MAINNET
    );
  }
  return (
    process.env.REACT_APP_BASE_UNISWAP_QUOTER || UNISWAP_V3_QUOTER_BASE_MAINNET
  );
}

function getUniswapFeeTiers(): number[] {
  // Base Sepolia pools are often thin/mispriced. Avoid 1% tier by default there.
  // We prioritize 0.3% and only then 0.05% as a fallback.
  if (EVM_CONFIG.CHAIN_ID === 84532) {
    return [3000, 500];
  }
  return DEFAULT_UNISWAP_FEE_TIERS;
}

async function getUniswapV3Quote(
  inputToken: TokenInfo,
  outputToken: TokenInfo,
  amountIn: bigint,
): Promise<{ amountOut: bigint; blockNumber: number; fee: number } | null> {
  const tokenIn = tokenToUniswapAddress(inputToken);
  const tokenOut = tokenToUniswapAddress(outputToken);
  if (tokenIn.toLowerCase() === tokenOut.toLowerCase()) return null;

  const provider = getBaseReadProvider();
  const quoterAddress = getUniswapQuoterAddress();
  const quoter = new ethers.Contract(
    quoterAddress,
    [
      "function quoteExactInputSingle(address tokenIn,address tokenOut,uint24 fee,uint256 amountIn,uint160 sqrtPriceLimitX96) returns (uint256 amountOut)",
      "function quoteExactInputSingle((address tokenIn,address tokenOut,uint256 amountIn,uint24 fee,uint160 sqrtPriceLimitX96) params) returns (uint256 amountOut,uint160 sqrtPriceX96After,uint32 initializedTicksCrossed,uint256 gasEstimate)",
    ],
    provider,
  );

  let best: bigint = 0n;
  let bestFee: number | null = null;
  console.info(`${SWAP_DEBUG_PREFIX} Uniswap quote start`, {
    chainId: EVM_CONFIG.CHAIN_ID,
    quoter: quoterAddress,
    tokenIn,
    tokenOut,
    amountIn: amountIn.toString(),
  });

  try {
    const network = await provider.getNetwork();
    console.info(`${SWAP_DEBUG_PREFIX} Quote RPC network detected`, {
      chainId: Number(network.chainId),
    });
  } catch (err) {
    console.error(`${SWAP_DEBUG_PREFIX} Quote RPC unavailable`, {
      err,
    });
    return null;
  }

  const feeTiers = getUniswapFeeTiers();
  console.info(`${SWAP_DEBUG_PREFIX} Using Uniswap fee tiers`, {
    chainId: EVM_CONFIG.CHAIN_ID,
    feeTiers,
  });
  for (const fee of feeTiers) {
    try {
      const fn = quoter.getFunction(
        "quoteExactInputSingle((address,address,uint256,uint24,uint160))",
      );
      const result = await fn.staticCall([
        tokenIn,
        tokenOut,
        amountIn,
        fee,
        0n,
      ]);
      const amountOut = BigInt(
        (result as any)[0]?.toString?.() ?? result?.toString?.() ?? "0",
      );
      console.info(`${SWAP_DEBUG_PREFIX} Uniswap fee quote`, {
        fee,
        amountOut: amountOut.toString(),
        mode: "tuple",
      });
      if (amountOut > best) {
        best = amountOut;
        bestFee = fee;
      }
      continue;
    } catch {
      console.debug(`${SWAP_DEBUG_PREFIX} Tuple quote signature failed`, {
        fee,
      });
    }

    try {
      const fn = quoter.getFunction(
        "quoteExactInputSingle(address,address,uint24,uint256,uint160)",
      );
      const result = await fn.staticCall(tokenIn, tokenOut, fee, amountIn, 0n);
      const amountOut = BigInt((result as any)?.toString?.() ?? "0");
      console.info(`${SWAP_DEBUG_PREFIX} Uniswap fee quote`, {
        fee,
        amountOut: amountOut.toString(),
        mode: "legacy",
      });
      if (amountOut > best) {
        best = amountOut;
        bestFee = fee;
      }
    } catch {
      console.warn(`${SWAP_DEBUG_PREFIX} Uniswap fee tier failed`, { fee });
    }
  }

  if (best <= 0n || bestFee == null) {
    console.error(
      `${SWAP_DEBUG_PREFIX} Uniswap quote failed for all fee tiers`,
    );
    return null;
  }
  const blockNumber = await provider.getBlockNumber().catch(() => 0);
  console.info(`${SWAP_DEBUG_PREFIX} Uniswap quote success`, {
    amountOut: best.toString(),
    fee: bestFee,
    blockNumber,
  });
  return { amountOut: best, blockNumber, fee: bestFee };
}

function getRelayerApiCandidates(): string[] {
  const candidates = [
    EVM_CONFIG.PROVER_URL,
    EVM_CONFIG.PROVER_DIRECT_URL,
    EVM_CONFIG.NOVA_PROVER_URL,
  ]
    .map((base) => (base || "").replace(/\/+$/, ""))
    .filter(Boolean);

  return [...new Set(candidates)];
}

function buildRelayerUrl(baseUrl: string, endpoint: string): string {
  return baseUrl.includes("?path=")
    ? `${baseUrl}${endpoint.replace(/^\//, "")}`
    : `${baseUrl}${endpoint}`;
}

async function getRelayerEOA(): Promise<string | null> {
  for (const baseUrl of getRelayerApiCandidates()) {
    try {
      const url = buildRelayerUrl(baseUrl, "/api/v1/relayer/address");
      const resp = await fetch(url);
      if (!resp.ok) {
        console.warn(`${SWAP_DEBUG_PREFIX} Relayer address lookup failed`, {
          baseUrl,
          status: resp.status,
        });
        continue;
      }
      const data = await resp.json();
      const addr = data.eoa_address || data.eoaAddress || null;
      if (addr) {
        console.info(`${SWAP_DEBUG_PREFIX} Relayer resolved`, {
          baseUrl,
          addr,
        });
        return addr;
      }
    } catch {
      console.warn(`${SWAP_DEBUG_PREFIX} Relayer address request threw`, {
        baseUrl,
      });
    }
  }
  console.error(
    `${SWAP_DEBUG_PREFIX} Relayer address not found on any candidate`,
    {
      candidates: getRelayerApiCandidates(),
    },
  );
  return null;
}

export function useBaseSwapQuote({
  notes = NO_NOTES,
  refreshKey = 0,
  enabled = true,
  locked = false,
  swapAmount,
  swapInputToken,
  swapOutputToken,
  swapSlippageBps,
  setSwapQuote,
  setSwapQuoteLoading,
  setSwapQuoteError,
}: UseBaseSwapQuoteParams) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (locked) return;
    let active = true;
    setSwapQuote(null);
    setSwapQuoteLoading(false);

    if (!enabled || !swapAmount || !swapInputToken || !swapOutputToken) {
      setSwapQuote(null);
      setSwapQuoteError(null);
      return;
    }

    let baseAmount: string;
    try {
      baseAmount = ethers
        .parseUnits(swapAmount, swapInputToken.decimals)
        .toString();
    } catch {
      setSwapQuoteError("Enter a valid amount");
      return;
    }
    if (BigInt(baseAmount) <= 0n) {
      setSwapQuote(null);
      setSwapQuoteError(null);
      return;
    }

    if (
      swapInputToken.address.toLowerCase() ===
      swapOutputToken.address.toLowerCase()
    ) {
      setSwapQuote(null);
      setSwapQuoteError("Input and output tokens must be different");
      return;
    }

    setSwapQuoteLoading(true);
    debounceRef.current = setTimeout(() => {
      const fetchQuote = async () => {
        setSwapQuoteLoading(true);
        setSwapQuoteError(null);
        try {
          if (EVM_CONFIG.SETTLEMENT_STACK === "flat_joinsplit_v2") {
            if (privateSwapDeployment()) {
              const source = privateSpendSource(
                notes,
                swapInputToken.address,
                BigInt(baseAmount),
              );
              if (source.amount < BigInt(baseAmount))
                throw Error("Insufficient available private balance");
              const preview = privateFeePreviewRequest({
                action: "withdraw",
                amount: swapAmount,
                recipient: ethers.ZeroAddress,
                token: swapInputToken.address,
                pool: source.pool,
                notes,
                decimals: swapInputToken.decimals,
                chainId: EVM_CONFIG.CHAIN_ID,
              });
              if (!preview || !("inputCount" in preview))
                throw Error("Refresh your balance before swapping");
              const swap = await requestPrivateFees<PrivateSwapFeeQuote>(
                "swap-quote",
                {
                  token: swapInputToken.address,
                  outputToken: swapOutputToken.address,
                  amount: baseAmount,
                  inputCount: preview.inputCount,
                },
                { pool: source.pool },
              );
              if (active)
                setSwapQuote({
                  privateFeesSwap: swap,
                  pathId: `private-fees:${source.pool}:${swap.feeTier}`,
                  outAmounts: [swap.amountOut],
                  outValues: [0],
                  gasEstimate: 0,
                  priceImpact: 0,
                  percentDiff: 0,
                  netOutValue: 0,
                  blockNumber: 0,
                });
              return;
            }
            const muchfiFee =
              EVM_CONFIG.CHAIN_ID === MUCHFI_CHIKYU.chainId
                ? await fetchMuchfiFee(
                    notes,
                    swapInputToken.address,
                    swapOutputToken.address,
                    BigInt(baseAmount),
                  )
                : null;
            const swap = await quotePrivateSwap({
              provider: getBaseReadProvider(),
              chainId: EVM_CONFIG.CHAIN_ID,
              pool: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
              inputToken: swapInputToken.address,
              outputToken: swapOutputToken.address,
              amount: BigInt(baseAmount),
              relayFeeBps: EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS,
              ...(muchfiFee ? { relayFee: muchfiFee.fee } : {}),
            });
            if (active)
              setSwapQuote({
                privateSwap: swap,
                pathId: `${swap.venue || "uniswap-direct"}:${swap.inputToken}:${swap.outputToken}:${swap.feeTier}`,
                outAmounts: [swap.grossOutput],
                outValues: [0],
                gasEstimate: 0,
                priceImpact: 0,
                percentDiff: 0,
                netOutValue: 0,
                blockNumber: swap.blockNumber,
              });
            return;
          }
          const relayerAddr = await getRelayerEOA();
          if (!active) return;
          if (!relayerAddr) {
            if (active) setSwapQuoteError("Relayer not available");
            return;
          }

          const slippagePercent = Number(swapSlippageBps) / 100;

          const body = {
            chainId: EVM_CONFIG.CHAIN_ID,
            inputTokens: [
              {
                tokenAddress: tokenToOdosAddress(swapInputToken),
                amount: baseAmount,
              },
            ],
            outputTokens: [
              {
                tokenAddress: tokenToOdosAddress(swapOutputToken),
                proportion: 1,
              },
            ],
            userAddr: relayerAddr,
            slippageLimitPercent: slippagePercent,
            compact: true,
          };

          let data: OdosQuoteResponse | null = null;
          const forceUniswapDirect = isUniswapDirectProvider(
            SWAP_PROVIDER_OVERRIDE,
          );
          const shouldTryOdos = SWAP_PROVIDER_OVERRIDE
            ? SWAP_PROVIDER_OVERRIDE === "odos"
            : EVM_CONFIG.CHAIN_ID === 8453;
          const shouldTryUniswapFirst = forceUniswapDirect;
          console.info(`${SWAP_DEBUG_PREFIX} Quote flow start`, {
            chainId: EVM_CONFIG.CHAIN_ID,
            shouldTryOdos,
            shouldTryUniswapFirst,
            providerOverride: SWAP_PROVIDER_OVERRIDE || "(none)",
            inputToken: swapInputToken.symbol,
            outputToken: swapOutputToken.symbol,
            amount: baseAmount,
          });

          // Optional force-Uniswap mode for deterministic pool-based routing.
          if (shouldTryUniswapFirst) {
            const uniQuote = await getUniswapV3Quote(
              swapInputToken,
              swapOutputToken,
              BigInt(baseAmount),
            );
            if (uniQuote) {
              data = {
                pathId: `uniswap-direct:${tokenToOdosAddress(swapInputToken)}:${tokenToOdosAddress(swapOutputToken)}:${uniQuote.fee}`,
                outAmounts: [uniQuote.amountOut.toString()],
                outValues: [0],
                gasEstimate: 250000,
                priceImpact: 0,
                percentDiff: 0,
                netOutValue: 0,
                blockNumber: uniQuote.blockNumber,
              };
              console.info(`${SWAP_DEBUG_PREFIX} Uniswap override selected`, {
                pathId: data.pathId,
                outAmount: data.outAmounts[0],
              });
            }
          }

          // Try Odos when selected/default and no quote has been found yet.
          if (!data?.pathId && shouldTryOdos) {
            try {
              const resp = await fetch("https://api.odos.xyz/sor/quote/v3", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
              });

              if (resp.ok) {
                data = await resp.json();
                console.info(`${SWAP_DEBUG_PREFIX} Odos quote success`, {
                  pathId: data?.pathId,
                  outAmount: data?.outAmounts?.[0],
                });
              } else {
                const text = await resp.text().catch(() => "");
                console.warn(`${SWAP_DEBUG_PREFIX} Odos quote failed`, {
                  status: resp.status,
                  body: text.slice(0, 300),
                });
              }
            } catch {
              console.warn(`${SWAP_DEBUG_PREFIX} Odos request threw`);
            }
          }

          // Fallback: use real Uniswap V3 Quoter on testnet when Odos is unavailable.
          // Base mainnet only uses direct Uniswap when explicitly configured above.
          if (
            !data?.pathId &&
            !forceUniswapDirect &&
            EVM_CONFIG.CHAIN_ID === 84532
          ) {
            const uniQuote = await getUniswapV3Quote(
              swapInputToken,
              swapOutputToken,
              BigInt(baseAmount),
            );
            if (uniQuote) {
              data = {
                pathId: `uniswap-direct:${tokenToOdosAddress(swapInputToken)}:${tokenToOdosAddress(swapOutputToken)}:${uniQuote.fee}`,
                outAmounts: [uniQuote.amountOut.toString()],
                outValues: [0],
                gasEstimate: 250000,
                priceImpact: 0,
                percentDiff: 0,
                netOutValue: 0,
                blockNumber: uniQuote.blockNumber,
              };
              console.info(`${SWAP_DEBUG_PREFIX} Uniswap fallback selected`, {
                pathId: data.pathId,
                outAmount: data.outAmounts[0],
              });
            }
          }

          if (active) {
            if (data?.pathId) {
              setSwapQuote(data);
              setSwapQuoteError(null);
            } else {
              setSwapQuote(null);
              setSwapQuoteError("No route available for this pair");
              console.error(
                `${SWAP_DEBUG_PREFIX} No route available after Odos/Uniswap attempts`,
              );
            }
          }
        } catch (err: any) {
          if (active) {
            setSwapQuote(null);
            setSwapQuoteError(err?.message || "Failed to fetch swap quote");
          }
        } finally {
          if (active) setSwapQuoteLoading(false);
        }
      };

      fetchQuote();
    }, DEBOUNCE_MS);

    return () => {
      active = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    notes,
    refreshKey,
    enabled,
    locked,
    swapAmount,
    swapInputToken,
    swapOutputToken,
    swapSlippageBps,
    setSwapQuote,
    setSwapQuoteLoading,
    setSwapQuoteError,
  ]);
}
